import { Component, createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";

// Brand Clerk here once: it styles sign-in, sign-up and the user menu. Dark app: `yarn add @clerk/ui@1.38.1`,
// `import { dark } from "@clerk/ui/themes"`, then add `theme: dark, captcha: { theme: "dark" }` (see Styling).
const appearance = {
  variables: {
    colorPrimary: "#0F172A",
    colorPrimaryForeground: "#FFFFFF",
    fontFamily: "'IBM Plex Sans', sans-serif",
    borderRadius: "0.75rem",
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// true quando Clerk (accesso Google) è pronto; il sito e il login staff funzionano anche senza
const ClerkReady = createContext(false);
export const useClerkReady = () => useContext(ClerkReady);

// Se Clerk va in errore (es. istanza di produzione non configurata) si continua senza accesso Google
class ClerkBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.error("Accesso Google non disponibile:", error?.message);
  }
  render() {
    return this.state.failed ? <ClerkReady.Provider value={false}>{this.props.fallback}</ClerkReady.Provider> : this.props.children;
  }
}

// Starts Clerk from the backend's runtime config, so deploy's live keys need no rebuild.
// Must render inside <BrowserRouter> (see step 3) so Clerk navigates without reloading.
export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  // Retries while the backend restarts (e.g. right after provisioning): the app's
  // Clerk components crash if they ever render without <ClerkProvider>.
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const cfg = await (await fetch(`${BACKEND_URL}/api/clerk/config`)).json();
        if (cfg.enabled) return cancelled || setConfig(cfg);
      } catch (err) {
        console.warn("Configurazione accesso non ancora disponibile, nuovo tentativo:", err?.message);
      }
      if (cancelled) return;
      if (attempt >= 20) return setUnavailable(true);
      setTimeout(() => load(attempt + 1), 1500);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (unavailable || !config) return <ClerkReady.Provider value={false}>{children}</ClerkReady.Provider>;
  return (
    <ClerkBoundary fallback={children}>
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={appearance}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      <ClerkReady.Provider value={true}>{children}</ClerkReady.Provider>
    </ClerkProvider>
    </ClerkBoundary>
  );
}
